import {
  Badge,
  Button,
  Card,
  Input,
  message,
  Popover,
  Select,
  Space,
  Table,
  Tooltip,
  Typography,
  Tabs,
  Empty,
  Tag,
  Descriptions,
  Modal,
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  FetchNextPageOptions,
  InfiniteQueryObserverResult,
} from '@tanstack/react-query';
import { formatDate } from '../../utils/format';
import { CreditCardOutlined, EyeOutlined, TruckOutlined } from '@ant-design/icons';
import InfiniteLoaderWrapper from '../InfiniteLoaderWrapper';
import { CardSchema, CardDispatchData } from '@/types/cards/card-types';
import { ProductOrder } from '@/types/cards/card-types';
import { ColumnsType } from 'antd/es/table';
import { getCardById, getProductOrderById } from '@/services/cards';

type TCardDataProps = {
  mode: 'single' | 'batch';
  cardData?: CardSchema[];
  batchData?: ProductOrder[];
  fetchNextPage: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isTableLoading: boolean;
  fetchNextPageBatch?: (
    options?: FetchNextPageOptions
  ) => Promise<InfiniteQueryObserverResult<any[], Error>>;
  hasNextPageBatch?: boolean;
  isFetchingNextPageBatch?: boolean;
};

function DispatchedCardsTable(props: TCardDataProps) {
  const [selectedItem, setSelectedItem] = useState<CardSchema | ProductOrder | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredData, setFilteredData] = useState<any[]>([]);

  const {
    mode,
    cardData,
    batchData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isTableLoading,
    fetchNextPageBatch,
    hasNextPageBatch,
    isFetchingNextPageBatch,
  } = props;

  const data = mode === 'single' ? cardData : batchData;
  const currentFetchNextPage = mode === 'single' ? fetchNextPage : fetchNextPageBatch!;
  const currentHasNextPage = mode === 'single' ? hasNextPage : hasNextPageBatch!;
  const currentIsFetchingNextPage = mode === 'single' ? isFetchingNextPage : isFetchingNextPageBatch!;

  const handleViewDispatchDetails = async (record: CardSchema | ProductOrder) => {
    // If it's a batch order (has product_identifier), fetch batch details
    if ('product_identifier' in record) {
      try {
        const batchWithDispatch = await getProductOrderById(record.id, true);
        setSelectedItem(batchWithDispatch.product_orders?.[0] || record);
      } catch (error) {
        setSelectedItem(record);
      }
    } else if ('id' in record) {
      // Single card
      try {
        const cardWithDispatch = await getCardById(record.id, true);
        setSelectedItem(cardWithDispatch);
      } catch (error) {
        setSelectedItem(record);
      }
    } else {
      setSelectedItem(record);
    }
    setIsModalVisible(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase();
    setSearchValue(e.target.value);

    if (!data) return;

    const filteredData = data.filter((item) => {
      if (mode === 'single') {
        const card = item as CardSchema;
        const reference = card.dispatch_info?.reference?.toLowerCase() || '';
        const holderName = card.holder_name?.toLowerCase() || '';
        const cardNumber = `${card.bin}${card.last_four}`.toLowerCase();
        
        return (
          reference.includes(searchTerm) ||
          holderName.includes(searchTerm) ||
          cardNumber.includes(searchTerm)
        );
      } else {
        const batch = item as ProductOrder;
        const accountName = batch.account_name?.toLowerCase() || '';
        
        return (
          accountName.includes(searchTerm)
        );
      }
    });
    setFilteredData(filteredData);
  };

  useEffect(() => {
    if (data) {
      setFilteredData(data);
    }
  }, [data]);

  // Debug: log the data passed to the table to check for delivery_date
  console.log('DispatchedCardsTable data:', filteredData);

  // Single cards columns
  const singleCardsColumns: ColumnsType<CardSchema> = [
    {
      title: 'Card Number',
      key: 'card_number',
      width: 180,
      render: (_: unknown, record: CardSchema) => (
        <Space>
          <span><Tag>{record.bin}</Tag> •••• {record.last_four}</span>
        </Space>
      ),
    },
    {
      title: 'Card Holder',
      dataIndex: 'holder_name',
      key: 'holder_name',
      width: 200,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 150,
      render: (date: string) => date ? formatDate(date) : 'N/A',
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      render: (date: number) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: CardSchema) => (
        <Button
          type='primary'
          icon={<EyeOutlined />}
          onClick={() => handleViewDispatchDetails(record)}
        >
          View Details
        </Button>
      ),
    },
  ];

  // Batch orders columns
  const batchOrdersColumns: ColumnsType<ProductOrder> = [
    {
      title: 'Batch ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
    },
    {
      title: 'Account Name',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 200,
    },

    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      width: 100,
    },
    {
      title: 'Delivery Date',
      dataIndex: 'delivery_date',
      key: 'delivery_date',
      width: 150,
      render: (date: string) => date ? formatDate(date) : 'N/A',
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      width: 150,
      render: (date: number) => formatDate(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: ProductOrder) => (
        <Button
          type='primary'
          icon={<EyeOutlined />}
          onClick={() => handleViewDispatchDetails(record)}
        >
          View Details
        </Button>
      ),
    },
  ];

  const columns = mode === 'single' ? singleCardsColumns : batchOrdersColumns;

  const renderDispatchDetails = () => {
    if (!selectedItem) return null;

    const dispatchInfo = (selectedItem as any).dispatch_info;
    const deliveryDate = (selectedItem as any).delivery_date;
    if (!dispatchInfo) {
      return <Empty description="No dispatch information available" />;
    }

    return (
      <Descriptions bordered column={1}>
        {deliveryDate && (
          <Descriptions.Item label="Delivery Date">{formatDate(deliveryDate)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Reference">{dispatchInfo.reference || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Type">{
          dispatchInfo.type
            ? (
                <Tag color={dispatchInfo.type.toLowerCase() === 'delivery' ? 'blue' : 'orange'}>
                  {dispatchInfo.type.toLowerCase() === 'delivery' ? 'DELIVERY' : 'PICK-UP'}
                </Tag>
              )
            : 'N/A'
        }</Descriptions.Item>
        <Descriptions.Item label="Status">{
          dispatchInfo.status
            ? (() => {
                const status = dispatchInfo.status.toUpperCase();
                let color = 'default';
                if (status === 'COMPLETED' || status === 'DELIVERED') color = 'green';
                else if (status === 'CANCELLED') color = 'red';
                else if (status === 'PENDING' || status === 'PENDING-COLLECTION') color = 'orange';
                return <Tag color={color}>{status}</Tag>;
              })()
            : 'N/A'
        }</Descriptions.Item>
        <Descriptions.Item label="Initiator">{dispatchInfo.initiator_name || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Initiator Email">{dispatchInfo.initiator_email || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Estimated Delivery Fee">
          {dispatchInfo.estimated_delivery_fee !== undefined && dispatchInfo.estimated_delivery_fee !== null
            ? `₦${dispatchInfo.estimated_delivery_fee.toLocaleString()}`
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Actual Delivery Fee">
          {dispatchInfo.actual_delivery_fee !== undefined && dispatchInfo.actual_delivery_fee !== null
            ? `₦${dispatchInfo.actual_delivery_fee.toLocaleString()}`
            : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Pickup Address">
          {dispatchInfo.pickup_address?.full_address || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Destination Address">
          {dispatchInfo.destination_address?.full_address || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Rider Name">
          {dispatchInfo.rider_info?.name || 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Rider Phone">
          {dispatchInfo.rider_info?.phone_number || 'N/A'}
        </Descriptions.Item>
        {dispatchInfo.events && dispatchInfo.events.length > 0 ? (
          <Descriptions.Item label="Events">
            <Space direction="vertical" style={{ width: '100%' }}>
              {dispatchInfo.events.map((event: any, index: number) => (
                <Card key={index} size="small">
                  <Typography.Text strong>{event.name ? event.name.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()) : 'N/A'}</Typography.Text>
                  <br />
                  <Typography.Text type="secondary">{event.description || 'N/A'}</Typography.Text>
                  <br />
                  <Typography.Text type="secondary">{event.timestamp ? formatDate(event.timestamp) : 'N/A'}</Typography.Text>
                </Card>
              ))}
            </Space>
          </Descriptions.Item>
        ) : (
          <Descriptions.Item label="Events">N/A</Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  return (
    <>
      <Card
        title={
          <Space>
            <TruckOutlined />
            <Typography.Title level={5}>
              {mode === 'single' ? 'Dispatched Single Cards' : 'Dispatched Batch Orders'}
            </Typography.Title>
          </Space>
        }
        style={{ marginTop: 24 }}
        extra={
          <Space>
            <Input
              placeholder={`Search by ${mode === 'single' ? 'card holder or reference' : 'account name'}`}
              value={searchValue}
              onChange={handleChange}
              style={{ width: '100%' }}
            />
          </Space>
        }
      >
        <InfiniteLoaderWrapper
          hasNextPage={currentHasNextPage}
          fetchNextPage={currentFetchNextPage}
          isFetchingNextPage={currentIsFetchingNextPage}
        >
          <Table
            dataSource={filteredData}
            columns={columns}
            rowKey='id'
            pagination={false}
            loading={isTableLoading}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={`No dispatched ${mode === 'single' ? 'cards' : 'batch orders'}`}
                />
              ),
            }}
          />
        </InfiniteLoaderWrapper>
      </Card>

      <Modal
        title="Dispatch Details"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {renderDispatchDetails()}
      </Modal>
    </>
  );
}

export default DispatchedCardsTable; 